import { useDateHelper, useTargetBrandConfig } from 'FrontRoyalAngular';
import { useSyncConfig } from 'FrontRoyalConfig';
import { useMemo } from 'react';
import { type AdmittedStudentInfo } from 'AppBranding';
import { type StatusConfig, type AdmittedStudentsZoomStatus } from './AdmittedStudentsZoomBox.types';

export function admittedStudentsZoomStatus(admittedStudentInfo: AdmittedStudentInfo): AdmittedStudentsZoomStatus {
    const { registrationUrl, recordingUrl, webinarDate } = admittedStudentInfo;

    if (!registrationUrl || !webinarDate) {
        return 'hidden';
    }

    if (registrationUrl && webinarDate > new Date()) {
        return 'saveTheDate';
    }

    const fourWeeks = 1000 * 60 * 60 * 24 * 28;
    const fourWeeksAgo = new Date(Date.now() - fourWeeks);
    if (recordingUrl && webinarDate > fourWeeksAgo) {
        return 'watchRecording';
    }

    return 'hidden';
}

export function useAdmittedStudentsZoomBoxConfig() {
    const config = useSyncConfig();
    const brandConfig = useTargetBrandConfig();
    const dateHelper = useDateHelper();

    const admittedStudentInfo = useMemo(() => brandConfig.admittedStudentInfo(config), [config, brandConfig]);

    const status = useMemo(() => admittedStudentsZoomStatus(admittedStudentInfo), [admittedStudentInfo]);

    const statusConfigs = useMemo<Record<AdmittedStudentsZoomStatus, StatusConfig>>(
        () => ({
            saveTheDate: {
                heading: { key: 'headingAdmittedStudentsZoomSaveTheDate' },
                button: { key: 'buttonViewAndSchedule' },
                text: {
                    key: 'contentAdmittedStudentsZoomYouAreInvited',
                    params: {
                        webinarMonthDay: dateHelper.formattedUserFacingMonthDayLong(
                            admittedStudentInfo.webinarDate!,
                            false,
                        ),
                    },
                },
                ctaUrl: admittedStudentInfo.registrationUrl,
            },
            watchRecording: {
                heading: { key: 'headingAdmittedStudentsZoomQAndA' },
                button: { key: 'watchRecording' },
                text: { key: 'contentAdmittedStudentsIfYouMissedTheZoom' },
                ctaUrl: admittedStudentInfo.recordingUrl,
            },
            hidden: {
                heading: null,
                button: null,
                text: null,
                ctaUrl: null,
            },
        }),
        [admittedStudentInfo, dateHelper],
    );

    const zoomBoxStatusConfig = useMemo(() => statusConfigs[status], [status, statusConfigs]);

    return { zoomBoxStatusConfig };
}
